import type { FormEvent, ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Car, Eraser, MapPin, UserRound } from "lucide-react";
import { Button } from "../../components/ui/Button.js";
import { Checkbox, Field, Input, Select } from "../../components/ui/Form.js";
import { titleCase } from "../../lib/format.js";
import { facetsQuery } from "../../queries.js";
import { EMPTY_ADVANCED, emailNameTypo, type AdvancedDraft, type AdvancedErrors } from "./advanced.js";
import { VehicleLookupHint } from "./VehicleLookupHint.js";

function Group({ icon, title, hint, children }: { icon: ReactNode; title: string; hint?: string; children: ReactNode }) {
  return (
    <fieldset className="rounded-lg border border-border bg-surface p-3 sm:p-4">
      <legend className="flex items-center gap-1.5 px-1 text-[13px] font-semibold text-fg [&_svg]:size-4 [&_svg]:text-subtle">
        {icon}
        {title}
      </legend>
      {hint && <p className="-mt-1 mb-3 text-[12px] text-subtle">{hint}</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </fieldset>
  );
}

/**
 * Búsqueda avanzada por campos. Ningún campo es obligatorio: los identificadores
 * (patente, VIN, RUT, teléfono, correo, nombre) encuentran el aviso; vehículo y
 * ubicación acotan; la fecha del siniestro ordena por cercanía (o filtra).
 */
export function AdvancedForm({ value, onChange, errors, onSubmit, loading }: {
  value: AdvancedDraft;
  onChange: (next: AdvancedDraft) => void;
  errors: AdvancedErrors;
  onSubmit: () => void;
  loading: boolean;
}) {
  const facets = useQuery(facetsQuery);
  const d = value;
  const set = <K extends keyof AdvancedDraft>(k: K, v: AdvancedDraft[K]) => onChange({ ...d, [k]: v });
  const text = (k: keyof AdvancedDraft, label: string, opts: { placeholder?: string; hint?: ReactNode; max?: number; mono?: boolean; span?: 2; type?: string; inputMode?: "numeric" | "email" | "tel" } = {}) => (
    <Field label={label} hint={opts.hint} error={errors[k]} className={opts.span === 2 ? "sm:col-span-2" : undefined}>
      {(p) => (
        <Input
          {...p}
          type={opts.type ?? "text"}
          inputMode={opts.inputMode}
          value={d[k] as string}
          onChange={(e) => set(k, e.target.value as never)}
          maxLength={opts.max ?? 80}
          placeholder={opts.placeholder}
          autoComplete="off"
          spellCheck={false}
          className={opts.mono ? "font-mono uppercase" : undefined}
        />
      )}
    </Field>
  );

  function submit(e: FormEvent) {
    e.preventDefault();
    onSubmit();
  }

  return (
    <form onSubmit={submit} noValidate aria-label="Búsqueda avanzada" className="space-y-3">
      <Group icon={<Car />} title="Vehículo" hint="Con patente o VIN se encuentra el aviso aunque diga otra marca (posible patente clonada).">
        {text("plate", "Patente", { placeholder: "BBCL12", mono: true, max: 12, hint: <VehicleLookupHint /> })}
        {text("vin", "VIN, chasis o n° de motor", { placeholder: "9BWZZZ377VT004251", mono: true, max: 25, hint: "Completo o parcial (6+ caracteres)" })}
        {text("make", "Marca", { placeholder: "Toyota", max: 40 })}
        {text("model", "Modelo", { placeholder: "Yaris", max: 40 })}
        {text("yearFrom", "Año desde", { placeholder: "2015", max: 4, inputMode: "numeric" })}
        {text("yearTo", "Año hasta", { placeholder: "2018", max: 4, inputMode: "numeric", hint: "Vacío = el mismo año" })}
        {text("color", "Color", { placeholder: "Rojo", max: 30 })}
        {text("keywords", "Palabras clave", { placeholder: "chocado, sin papeles…", max: 120 })}
      </Group>

      <Group icon={<CalendarClock />} title="Siniestro"
        hint="Lo publicado antes puede mostrar el vehículo ya dañado o a la venta; lo publicado después, su reventa o la noticia del robo. Útil sobre todo con grupos de noticias y comunidades.">
        {text("incidentDate", "Fecha del siniestro", { type: "date" })}
        {text("daysBefore", "Días antes", { max: 3, inputMode: "numeric", hint: "Por defecto 30" })}
        {text("daysAfter", "Días después", { max: 3, inputMode: "numeric", hint: "Por defecto 60" })}
        <div className="flex items-end pb-2">
          <Checkbox
            checked={d.onlyWindow}
            disabled={!d.incidentDate}
            onChange={(e) => set("onlyWindow", e.target.checked)}
            label="Solo dentro de esas fechas"
          />
        </div>
      </Group>

      <Group icon={<UserRound />} title="Personas y contacto" hint="Varios valores: sepárelos con coma.">
        {text("name", "Nombre", { placeholder: "Juan Pérez", span: 2 })}
        {text("ruts", "RUT", { placeholder: "12.345.678-5", max: 80 })}
        {text("phones", "Teléfono", { placeholder: "+56 9 1234 5678", max: 100, inputMode: "tel" })}
        {text("emails", "Correo", {
          placeholder: "nombre@correo.cl", max: 300, span: 2, inputMode: "email",
          hint: emailNameTypo(d.name, d.emails) ?? "También se busca lo que va antes de @ como cuenta en redes.",
        })}
      </Group>

      <Group icon={<MapPin />} title="Ubicación"
        hint="La dirección ordena los resultados (los avisos rara vez la traen completa). Su calle también se busca en noticias del robo o choque del vehículo, nunca junto al nombre.">
        <Field label="Región" error={errors.region}>
          {(p) => (
            <Select {...p} value={d.region} onChange={(e) => set("region", e.target.value)}>
              <option value="">Todas</option>
              {(facets.data?.regions ?? []).map((r) => (
                <option key={r.value} value={r.value}>{titleCase(r.value)}</option>
              ))}
            </Select>
          )}
        </Field>
        {text("comuna", "Comuna", { placeholder: "Maipú", max: 60 })}
        {text("address", "Dirección o lugar del siniestro", { placeholder: "Av. Pajaritos 1234", max: 120, span: 2 })}
      </Group>

      {errors.form && <p role="alert" className="text-[13px] font-medium text-danger">{errors.form}</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" variant="primary" size="lg" loading={loading}>Buscar</Button>
        <Button variant="ghost" icon={<Eraser className="size-4" aria-hidden="true" />} onClick={() => onChange(EMPTY_ADVANCED)}>
          Limpiar
        </Button>
        <p className="text-[12px] text-subtle sm:ml-auto">
          Ningún campo es obligatorio. Si no hay avisos con la patente o el nombre, se muestran los que calzan con el resto.
        </p>
      </div>
    </form>
  );
}
